import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * Where payouts go — plan-005 OP-38 (FR-MNY-5; AC-MNY-5, UI half; design §18.8.1, IC-52b, IC-59).
 *
 * WHO: Owners and Admins see the destination; only the Owner changes it (OD-P5). A Viewer is told
 * who can, rather than shown a read that the API refuses.
 *
 * THE IBAN IS SHOWN AS ITS LAST FOUR CHARACTERS, AND NOWHERE ELSE. The API never returns more
 * (`iban_last_four`), so the page cannot show more; the form holds the typed IBAN only while the
 * dialog is open, sends it once in the PUT body, and clears it on success, on close and on cancel.
 * It is never logged, never put in a URL, a query key or storage, and never echoed in an error:
 * the API names the failing field and nothing else (`payout_destination_invalid`), and so does this.
 *
 * STEP-UP: a change needs the person's password re-entered within a few minutes. The API answers
 * `401 step_up_required` otherwise; the form then asks for the password (`POST /v1/session/step-up`)
 * and retries the same change ONCE. A second refusal is shown, never looped. While WeTalk support
 * is acting as the person the API answers `403 step_up_unavailable`, and the form says only the
 * person can do it.
 *
 * THE HOLD: a new destination is unverified until WeTalk checks it, and payouts wait for the
 * destination-change hold (7 days today — the figure comes from the status, never from here).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { validatePayoutDestination, type PayoutDestinationField } from "@wetalk/domain";
import { Button, Dialog, Field, Icon, Input, Tag } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { apiProblemText } from "../../api/api-error.js";
import type { WorkspaceRoleView } from "../../api/gateway.js";
import {
  PAYOUT_DESTINATION_CHANGE_ROLE,
  maskedIbanText,
  mayChangePayoutDestination,
  mayReadPayoutDestination,
  type PayoutDestinationChangeView,
  type PayoutDestinationView,
} from "../../api/slice/payout.js";
import { GLYPH } from "../../shell/glyph.js";
import { rolesInPlainWords } from "../../shell/member-capability.js";
import { athensDateText } from "./payout-copy.js";
import { PAYOUT_QUERY_KEY, payoutDestinationQuery } from "./payout-query.js";

/** Each form field's words when the rules refuse it — the value itself is never repeated. */
export const DESTINATION_FIELD_PROBLEM: Readonly<Record<PayoutDestinationField, string>> = {
  get beneficiary_name() { return portalText("finance", "m1183"); },
  get iban() { return portalText("finance", "m1184"); },
  get bic() { return portalText("finance", "m1185"); },
  get tax_identifier() { return portalText("finance", "m1186"); },
};

const FIELD_SEQUENCE: readonly PayoutDestinationField[] = ["beneficiary_name", "iban", "bic", "tax_identifier"];

function isDestinationField(value: string | null): value is PayoutDestinationField {
  return value !== null && (FIELD_SEQUENCE as readonly string[]).includes(value);
}

/** "payouts wait 7 days" — or, while the status has not arrived, no figure at all (never a guess). */
function holdText(day: number | null): string {
  if (day === null) return portalText("finance", "m0768");
  return portalText("finance", "payout.waitDays", { count: day });
}

interface DestinationDraft {
  readonly beneficiaryName: string;
  readonly iban: string;
  readonly bic: string;
  readonly taxIdentifier: string;
}

const EMPTY_DRAFT: DestinationDraft = { beneficiaryName: "", iban: "", bic: "", taxIdentifier: "" };

export interface PayoutDestinationPanelProps {
  readonly role: WorkspaceRoleView | null;
  /** Days payouts wait after a change (`destination_change_hold_day`), from the status. */
  readonly changeHoldDay: number | null;
}

export function PayoutDestinationPanel({ role, changeHoldDay }: PayoutDestinationPanelProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const readable = mayReadPayoutDestination(role);
  const destination = useQuery({ ...payoutDestinationQuery(gateway), enabled: readable });
  const [open, setOpen] = useState(false);

  if (role === null) return null;
  if (!readable) {
    return (
      <section className="po-section" aria-labelledby="po-destination-title">
        <h2 id="po-destination-title" className="po-section-title">{portalText("finance", "m0770")}</h2>
        <p className="c-note">{portalText("finance", "m0771")}</p>
      </section>
    );
  }

  const current = destination.data ?? null;
  const changeable = mayChangePayoutDestination(role);

  return (
    <section className="po-section" aria-labelledby="po-destination-title">
      <div className="po-section-head">
        <h2 id="po-destination-title" className="po-section-title">{portalText("finance", "m0770")}</h2>
        {changeable ? (
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(true);
            }}
          >
            {current === null ? portalText("finance", "m0772") : portalText("finance", "m0773")}
          </Button>
        ) : null}
      </div>

      {destination.isError ? (
        <p className="c-note" role="alert">
          {apiProblemText(destination.error, portalText("finance", "m0774"))}
        </p>
      ) : destination.data === undefined ? (
        <p className="c-note" role="status">{portalText("finance", "m0775")}</p>
      ) : current === null ? (
        <p className="c-note">{portalText("finance", "m0776")}</p>
      ) : (
        <DestinationFacts destination={current} />
      )}

      {changeable ? null : (
        <p className="po-muted">{portalText("finance", "m0777")}{rolesInPlainWords(PAYOUT_DESTINATION_CHANGE_ROLE)}{portalText("finance", "m0778")}</p>
      )}

      {changeable ? (
        <DestinationDialog
          open={open}
          changing={current !== null}
          changeHoldDay={changeHoldDay}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

function DestinationFacts({ destination }: { readonly destination: PayoutDestinationView }): ReactNode {
  usePortalTranslation("finance");
  const now = new Date();
  const holding = destination.holdUntil !== null && destination.holdUntil > now;
  return (
    <>
      <dl className="po-facts">
        <div className="po-fact">
          <dt>{portalText("finance", "m0779")}</dt>
          <dd>{destination.beneficiaryName}</dd>
        </div>
        <div className="po-fact">
          <dt>{portalText("finance", "m0780")}</dt>
          <dd className="po-mono" aria-label={portalText("finance", "m0781", { value1: destination.ibanLastFour })}>
            {maskedIbanText(destination.ibanLastFour)}
          </dd>
        </div>
        <div className="po-fact">
          <dt>{portalText("finance", "m0502")}</dt>
          <dd>{destination.countryCode}</dd>
        </div>
        {destination.bic === null ? null : (
          <div className="po-fact">
            <dt>{portalText("finance", "m0782")}</dt>
            <dd className="po-mono">{destination.bic}</dd>
          </div>
        )}
        <div className="po-fact">
          <dt>{portalText("finance", "m0783")}</dt>
          <dd className="po-mono">•••{destination.taxIdentifierLastThree}</dd>
        </div>
        <div className="po-fact">
          <dt>{portalText("finance", "m0784")}</dt>
          <dd>
            {destination.verified ? (
              <Tag status="positive">{portalText("finance", "m0785")}{destination.verifiedAt === null ? "" : ` · ${athensDateText(destination.verifiedAt)}`}
              </Tag>
            ) : (
              <Tag status="caution">{portalText("finance", "m0786")}</Tag>
            )}
          </dd>
        </div>
      </dl>
      {holding && destination.holdUntil !== null ? (
        <p className="po-notice" role="status"><Icon glyph={GLYPH.clockCountdown} size="md" /><span>{portalText("finance", "m0787")}{athensDateText(destination.holdUntil)}{portalText("finance", "m0788")}</span></p>
      ) : null}
    </>
  );
}

interface DestinationDialogProps {
  readonly open: boolean;
  readonly changing: boolean;
  readonly changeHoldDay: number | null;
  readonly onClose: () => void;
}

/** What the change is waiting on: nothing, or the person's password. */
type Stage = { readonly kind: "form" } | { readonly kind: "step_up"; readonly change: PayoutDestinationChangeView };

function DestinationDialog({ open, changing, changeHoldDay, onClose }: DestinationDialogProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<DestinationDraft>(EMPTY_DRAFT);
  const [fieldProblem, setFieldProblem] = useState<PayoutDestinationField | null>(null);
  const [problem, setProblem] = usePortalMessage();
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [password, setPassword] = useState("");

  // Declared before `close`, which resets them: a mutation keeps its `variables` — here the full IBAN
  // and, for the step-up, the password — for as long as it is observed, and this dialog stays mounted
  // while it is closed. `gcTime: 0` drops each from the mutation cache as soon as it is reset.
  const save = useMutation({
    gcTime: 0,
    mutationFn: (change: PayoutDestinationChangeView) => gateway.setPayoutDestination(change),
    onSuccess: () => saved(),
    onError: (error, change) => {
      if (error instanceof ApiError && error.code === "step_up_required") {
        setProblem(null);
        setStage({ kind: "step_up", change });
        return;
      }
      refused(error);
    },
  });

  // After the password: the same change, once. A second `step_up_required` is shown, not looped.
  const confirmAndSave = useMutation({
    gcTime: 0,
    mutationFn: async (input: { readonly password: string; readonly change: PayoutDestinationChangeView }) => {
      await gateway.stepUpAuthSession(input.password);
      return gateway.setPayoutDestination(input.change);
    },
    onSuccess: () => saved(),
    onError: (error) => {
      setPassword("");
      if (error instanceof ApiError && error.code === "credential_invalid") {
        setProblem(() => (portalText("finance", "m0789")));
        return;
      }
      refused(error);
    },
  });

  const close = (): void => {
    // The typed IBAN goes with the dialog: nothing of it outlives the form — neither the draft, nor
    // the pending step-up change, nor the mutations' remembered variables.
    setDraft(EMPTY_DRAFT);
    setPassword("");
    setFieldProblem(null);
    setProblem(null);
    setStage({ kind: "form" });
    save.reset();
    confirmAndSave.reset();
    onClose();
  };

  const saved = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PAYOUT_QUERY_KEY.payoutDestination }),
      queryClient.invalidateQueries({ queryKey: PAYOUT_QUERY_KEY.onlinePaymentStatus }),
      queryClient.invalidateQueries({ queryKey: PAYOUT_QUERY_KEY.payoutStatement }),
    ]);
    close();
  };

  /** A refusal in words; `step_up_required` is not one — it opens the password step instead. */
  const refused = (error: unknown): void => {
    if (error instanceof ApiError && error.code === "payout_destination_invalid" && isDestinationField(error.field)) {
      setStage({ kind: "form" });
      setFieldProblem(error.field);
      setProblem(null);
      return;
    }
    if (error instanceof ApiError && error.code === "step_up_unavailable") {
      setStage({ kind: "form" });
      setProblem(() => (portalText("finance", "m0790")));
      return;
    }
    if (error instanceof ApiError && error.code === "step_up_required") {
      setStage({ kind: "form" });
      setProblem(() => (portalText("finance", "m0791")));
      return;
    }
    setProblem(() => (apiProblemText(error, portalText("finance", "m0792"))));
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const bic = draft.bic.trim() === "" ? null : draft.bic;
    const checked = validatePayoutDestination({
      iban: draft.iban,
      bic,
      beneficiaryName: draft.beneficiaryName,
      taxIdentifier: draft.taxIdentifier,
    });
    if (!checked.ok) {
      setFieldProblem(checked.field);
      setProblem(null);
      return;
    }
    setFieldProblem(null);
    setProblem(null);
    save.mutate({
      beneficiaryName: checked.destination.beneficiaryName,
      iban: checked.destination.iban,
      bic: checked.destination.bic,
      taxIdentifier: checked.destination.taxIdentifier,
    });
  };

  const confirm = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (stage.kind !== "step_up") return;
    if (password === "") {
      setProblem(() => (portalText("finance", "m0793")));
      return;
    }
    setProblem(null);
    confirmAndSave.mutate({ password, change: stage.change });
  };

  /** The field's `error` prop — present only when that field is the one refused. */
  const errorOf = (field: PayoutDestinationField): { readonly error?: string } =>
    fieldProblem === field ? { error: DESTINATION_FIELD_PROBLEM[field] } : {};

  if (stage.kind === "step_up") {
    return (
      <Dialog
        open={open}
        title={portalText("finance", "m0794")}
        onClose={close}
        actions={
          <>
            <Button variant="ghost" onClick={close}>{portalText("finance", "m0418")}</Button>
            <Button variant="primary" type="submit" form="po-step-up-form" disabled={confirmAndSave.isPending}>
              {confirmAndSave.isPending ? portalText("finance", "m0795") : portalText("finance", "m0796")}
            </Button>
          </>
        }
      >
        <form id="po-step-up-form" className="po-form" onSubmit={confirm}>
          <p className="po-dialog-dek">{portalText("finance", "m0797")}</p>
          <Field label={portalText("finance", "m0798")}>
            {(control) => (
              <Input
                {...control}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.currentTarget.value);
                }}
              />
            )}
          </Field>
          {problem === null ? null : (
            <p className="po-problem" role="alert">
              {problem}
            </p>
          )}
        </form>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      title={changing ? portalText("finance", "m0773") : portalText("finance", "m0772")}
      onClose={close}
      actions={
        <>
          <Button variant="ghost" onClick={close}>{portalText("finance", "m0418")}</Button>
          <Button variant="primary" type="submit" form="po-destination-form" disabled={save.isPending}>
            {save.isPending ? portalText("finance", "m0484") : portalText("finance", "m0799")}
          </Button>
        </>
      }
    >
      <form id="po-destination-form" className="po-form" onSubmit={submit} autoComplete="off" noValidate>
        <p className="po-dialog-dek">{portalText("finance", "m0800")}{holdText(changeHoldDay)}{portalText("finance", "m0801")}</p>
        <Field label={portalText("finance", "m0802")} {...errorOf("beneficiary_name")}>
          {(control) => (
            <Input
              {...control}
              value={draft.beneficiaryName}
              maxLength={140}
              onChange={(event) => {
                const beneficiaryName = event.currentTarget.value;
                setDraft((one) => ({ ...one, beneficiaryName }));
              }}
            />
          )}
        </Field>
        <Field label={portalText("finance", "m0780")} {...errorOf("iban")}>
          {(control) => (
            <Input
              {...control}
              value={draft.iban}
              autoComplete="off"
              spellCheck={false}
              inputMode="text"
              onChange={(event) => {
                const iban = event.currentTarget.value;
                setDraft((one) => ({ ...one, iban }));
              }}
            />
          )}
        </Field>
        <Field
          label={portalText("finance", "m0803")}
          description={portalText("finance", "m0804")}
          {...errorOf("bic")}
        >
          {(control) => (
            <Input
              {...control}
              value={draft.bic}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => {
                const bic = event.currentTarget.value;
                setDraft((one) => ({ ...one, bic }));
              }}
            />
          )}
        </Field>
        <Field label={portalText("finance", "m0783")} description={portalText("finance", "m0805")} {...errorOf("tax_identifier")}>
          {(control) => (
            <Input
              {...control}
              value={draft.taxIdentifier}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => {
                const taxIdentifier = event.currentTarget.value;
                setDraft((one) => ({ ...one, taxIdentifier }));
              }}
            />
          )}
        </Field>
        {problem === null ? null : (
          <p className="po-problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}
